/* eslint @typescript-eslint/no-explicit-any: 0 */

import QuestionForm from "./QuestionForm";
import { Question } from "../../types/Question";
import UserAvatar from "../generic/UserAvatar";
import IconText from "../generic/IconText";
import TagDisplay from "../generic/TagDisplay";
import VoteDisplay from "../generic/VoteDisplay";
import UserName from "../generic/UserName";
import BasicMenu from "../generic/BasicMenu";
import { useAuth } from "../../contexts/auth.context";
import { Dictionary } from "../../../common/utils/types";
import {
    useDeleteQuestionMutation,
    useUpdateQuestionMutation,
    useVoteQuestionMutation,
} from "../../../features/api/questionSlice";

import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import ChatIcon from "@mui/icons-material/Chat";
import React from "react";
import { useNavigate } from "react-router-dom";

interface QuestionDetailsProps {
    question: Question;
}

const QuestionDetails: React.FC<QuestionDetailsProps> = ({ question }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [editMode, setEditMode] = React.useState<boolean>(false);
    const [localQuestion, setLocalQuestion] = React.useState<Question>(question);
    const author = question.author;

    const [deleteQuestion, { isLoading: loadingDeleteQuestion }] = useDeleteQuestionMutation();
    const [updateQuestion, { isLoading: loadingUpdateQuestion }] = useUpdateQuestionMutation();
    const [voteQuestion, { isLoading: loadingVoteQuestion }] = useVoteQuestionMutation();

    async function handleDelete() {
        if (!loadingDeleteQuestion) {
            try {
                console.log("Deleting question");
                await deleteQuestion(question.id).unwrap();
                navigate("/forum");
            } catch (error) {
                console.log(error);
            }
        }
    }

    function handleEdit() {
        setEditMode(true);
    }

    function handleEditCancel() {
        setLocalQuestion(question);
        setEditMode(false);
    }

    async function handleEditSubmit() {
        console.log("Submiting edited question");
        if (!loadingUpdateQuestion) {
            try {
                updateQuestion(localQuestion).unwrap();
                setEditMode(false);
                return true;
            } catch (error: any) {
                if (error.status === 400) {
                    return false;
                } else {
                    throw error;
                }
            }
        }
        return true;
    }

    async function handleVote(vote: number) {
        if (user.id === -1) {
            navigate("/signin");
            return;
        }
        if (!loadingVoteQuestion) {
            try {
                voteQuestion({ question, vote }).unwrap();
            } catch (error) {
                console.log(error);
            }
        }
    }

    const options: Dictionary<() => void> = {};

    if (user.id === author.id) {
        options.Edit = handleEdit;
        options.Delete = handleDelete;
    }

    const questionContent = (
        <>
            <Typography variant="h4" color={"black"} padding={1}>
                {question.title}
            </Typography>
            {/* Tags */}
            <TagDisplay tags={question.tags} />
            <Divider />
            <Typography variant="body1" sx={{ fontSize: 18, whiteSpace: "pre-wrap" }} padding={2}>
                {question.body}
            </Typography>
        </>
    );

    return (
        <Card sx={{ display: "grid" }}>
            <CardHeader
                avatar={<UserAvatar user={author} size={20} />}
                action={<BasicMenu options={options} />}
                title={<UserName user={author} />}
                subheader={question.createdAt}
                sx={{ textAlign: "left", paddingBottom: 0 }}
            />
            <CardContent sx={{ textAlign: "left", paddingLeft: 2, paddingBottom: 0 }}>
                {editMode ? (
                    <QuestionForm
                        question={localQuestion}
                        onQuestionChange={setLocalQuestion}
                        onSubmit={handleEditSubmit}
                        onCancel={handleEditCancel}
                    />
                ) : (
                    questionContent
                )}
            </CardContent>
            <CardActions sx={{ display: "grid", p: 1 }}>
                {/* votes, answers */}
                <Stack direction={"row"} spacing={3} color={"dimgray"} alignItems={"center"} p={1}>
                    <VoteDisplay votes={question.votes} onVote={handleVote} currentVote={question.userVote} />
                    <IconText
                        icon={<ChatIcon color={question.acceptedAnswer !== -1 ? "success" : "inherit"} />}
                        text={question.answersCount.toString()}
                    />
                </Stack>
            </CardActions>
        </Card>
    );
};

export default QuestionDetails;
